import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { masterDataAPI, territoryMasterAPI, categoryMasterAPI } from "../services/api";
import {
  parseApiEnvelope,
  extractZonesFromResponse,
  extractRegionsFromResponse,
  normalizeZoneRow,
  normalizeRegionRow,
  normalizeTerritoryRow,
  mapZoneToOption,
  mapExportZonesToOptions,
  mapRegionToOption,
  mapTerritoryToOption,
  formatZoneOptionLabel,
  isExportMarketSelected,
  resolveRegionGeoCandidates,
  resolveZoneCodeForCountry,
  getCountryCodesForSelectedZones,
} from "../utils/dashboardGeoFilterUtils";
import { isGeoAllSelectionToken, USER_DEFAULT_GEO_CODE } from "../utils/userTerritoryScope";

const GEO_ALL_OPTION = { id: USER_DEFAULT_GEO_CODE, text: "All" };

const isGeoAllOptionRow = (id, text) => {
  if (isGeoAllSelectionToken(id)) return true;
  return String(text ?? "").trim().toLowerCase() === "all";
};

const selectionIncludesGeoAll = (selected, optionList = []) => {
  const ids = Array.isArray(selected) ? selected : [];
  return ids.some((id) => {
    const sid = String(id ?? "").trim();
    if (!sid) return false;
    if (isGeoAllSelectionToken(sid)) return true;
    const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === sid);
    return isGeoAllOptionRow(sid, opt?.text);
  });
};

const isGeoAllFilterValue = (val) =>
  Array.isArray(val) && val.length > 0 && val.every((id) => isGeoAllSelectionToken(id));

const ROLE_ALL_OPTION = { id: "all", text: "All" };

const isRoleAllToken = (id) => String(id ?? "").trim().toLowerCase() === "all";

const isRoleAllFilterValue = (val) =>
  Array.isArray(val) && val.length > 0 && val.every((id) => isRoleAllToken(id));

const EMPTY_GEO = {
  market: "",
  bv: [],
  role: [],
  country: [],
  zone: [],
  region: [],
  territory: [],
};

const toCsv = (val) => {
  if (!Array.isArray(val) || !val.length) return undefined;
  if (isGeoAllFilterValue(val)) return undefined;
  const real = val.map((x) => String(x ?? "").trim()).filter((id) => id && !isGeoAllSelectionToken(id));
  return real.length ? real.join(",") : undefined;
};

const toRoleCsv = (val) => {
  if (!Array.isArray(val) || !val.length) return undefined;
  if (isRoleAllFilterValue(val)) return undefined;
  const real = val
    .map((x) => String(x ?? "").trim())
    .filter((id) => id && !isRoleAllToken(id));
  return real.length ? real.join(",") : undefined;
};

export function useUserManagementFilters() {
  const [filterExpanded, setFilterExpanded] = useState(true);
  const [filters, setFilters] = useState(EMPTY_GEO);
  const [marketOptions, setMarketOptions] = useState([]);
  const [bvOptions, setBvOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([ROLE_ALL_OPTION]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [geoLoading, setGeoLoading] = useState({
    country: false,
    zone: false,
    region: false,
    territory: false,
  });

  const zoneOptionsRef = useRef([]);
  const regionOptionsRef = useRef([]);
  const prevExportCascadeRef = useRef(null);

  useEffect(() => {
    zoneOptionsRef.current = zoneOptions;
  }, [zoneOptions]);
  useEffect(() => {
    regionOptionsRef.current = regionOptions;
  }, [regionOptions]);

  const isExportCascade = useMemo(
    () => isExportMarketSelected(filters.market, marketOptions),
    [filters.market, marketOptions]
  );

  const exportCountryOptions = useMemo(() => {
    if (!isExportCascade || !filters.zone?.length) return countryOptions;
    if (isGeoAllFilterValue(filters.zone)) return countryOptions;
    const codes = getCountryCodesForSelectedZones(filters.zone, zoneOptions);
    if (!codes.size) return countryOptions;
    return countryOptions.filter((c) => codes.has(c.id) || isGeoAllSelectionToken(c.id));
  }, [isExportCascade, filters.zone, zoneOptions, countryOptions]);

  const runGeoDropdownLoad = async (level, task) => {
    setGeoLoading((prev) => ({ ...prev, [level]: true }));
    try {
      await task();
    } finally {
      setGeoLoading((prev) => ({ ...prev, [level]: false }));
    }
  };

  const loadMarketOptions = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      const { payload } = parseApiEnvelope(response);
      const markets = payload?.markets || payload?.Markets || payload || [];
      const options = (Array.isArray(markets) ? markets : [])
        .filter((m) => m.isActive !== false && m.IsActive !== false)
        .map((m) => ({
          id: m.marketCode || m.MARKETCODE || m.MarketCode,
          text: m.marketName || m.MARKETNAME || m.MarketName || m.marketCode,
        }))
        .filter((o) => o.id);
      setMarketOptions(options);
    } catch (e) {
      console.error("Error loading Market options:", e);
      setMarketOptions([]);
    }
  };

  const loadRoleOptions = async () => {
    try {
      const response = await masterDataAPI.getUserTypes();
      const { ok, payload } = parseApiEnvelope(response);
      const raw = ok && Array.isArray(payload) ? payload : [];
      const options = raw
        .filter((t) => t.isActive !== false && t.IsActive !== false)
        .map((t) => ({
          id: String(t.userTypeCode || t.UserTypeCode || "").trim(),
          text: String(
            t.userTypeName || t.UserTypeName || t.userTypeCode || t.UserTypeCode || ""
          ).trim(),
        }))
        .filter((o) => o.id)
        .sort((a, b) => a.text.localeCompare(b.text));
      setRoleOptions([ROLE_ALL_OPTION, ...options]);
    } catch (e) {
      console.error("Error loading Role options:", e);
      setRoleOptions([ROLE_ALL_OPTION]);
    }
  };

  const loadBVOptions = async (marketCode) => {
    try {
      const catRes = await categoryMasterAPI.getPaged({
        marketCode: marketCode && !isGeoAllSelectionToken(marketCode) ? marketCode : undefined,
        isActive: true,
        pageNumber: 1,
        pageSize: 5000,
      });
      const cats =
        catRes?.data?.Categories ||
        catRes?.data?.categories ||
        catRes?.data?.data?.Categories ||
        [];
      const options = (Array.isArray(cats) ? cats : [])
        .map((c) => ({
          id: c.catgCode || c.CATGCODE || c.CatgCode,
          text: c.catgName || c.CATGNAME || c.CatgName || c.catgCode,
        }))
        .filter((o) => o.id && !isGeoAllOptionRow(o.id, o.text));
      setBvOptions([GEO_ALL_OPTION, ...options]);
    } catch (e) {
      console.error("Error loading BV options:", e);
      setBvOptions([GEO_ALL_OPTION]);
    }
  };

  const loadCountryOptions = async () => {
    await runGeoDropdownLoad("country", async () => {
      try {
        const response = await masterDataAPI.getCountries();
        const { ok, payload } = parseApiEnvelope(response);
        const raw = ok && Array.isArray(payload) ? payload : [];
        const byId = new Map();
        raw.forEach((country) => {
          const id = String(country.countryCode ?? country.CountryCode ?? "").trim();
          const text = String(country.countryName ?? country.CountryName ?? "").trim();
          if (!id) return;
          if (isGeoAllOptionRow(id, text)) {
            byId.set(USER_DEFAULT_GEO_CODE, GEO_ALL_OPTION);
            return;
          }
          byId.set(id, { id, text: text || id });
        });
        if (!byId.has(USER_DEFAULT_GEO_CODE)) {
          byId.set(USER_DEFAULT_GEO_CODE, GEO_ALL_OPTION);
        }
        setCountryOptions([...byId.values()]);
      } catch (e) {
        console.error("Error loading Country options:", e);
        setCountryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadZoneOptions = async (countryCodes) => {
    await runGeoDropdownLoad("zone", async () => {
      try {
        const merged = [];
        const codes = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!codes.length) {
          const res = await territoryMasterAPI.getZones();
          const raw = extractZonesFromResponse(res);
          const active = Array.isArray(raw)
            ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
            : [];
          active.forEach((z) =>
            merged.push(normalizeZoneRow(z, z.countryCode || z.CountryCode))
          );
        } else {
          const zoneResults = await Promise.all(
            codes.map(async (cc) => {
              const res = await territoryMasterAPI.getZones(cc);
              const raw = extractZonesFromResponse(res);
              const active = Array.isArray(raw)
                ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
                : [];
              return active.map((z) => normalizeZoneRow(z, cc));
            })
          );
          zoneResults.flat().forEach((z) => merged.push(z));
        }
        const activeRows = merged.filter(
          (z) => z.zoneCode && !isGeoAllOptionRow(z.zoneCode, z.zoneName)
        );
        const mapped = isExportCascade
          ? mapExportZonesToOptions(activeRows, countryOptions)
          : (() => {
              const byZone = new Map();
              activeRows.forEach((z) => {
                byZone.set(z.zoneCode, mapZoneToOption(z, countryOptions));
              });
              return [...byZone.values()];
            })();
        setZoneOptions([GEO_ALL_OPTION, ...mapped]);
      } catch (e) {
        console.error("Error loading Zone options:", e);
        setZoneOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadRegionOptions = async (zoneCodes, countryCodes, zonesList, exportMode) => {
    await runGeoDropdownLoad("region", async () => {
      try {
        const zSel = Array.isArray(zoneCodes)
          ? zoneCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        const cSel = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!zSel.length || (exportMode && !cSel.length)) {
          regionOptionsRef.current = [GEO_ALL_OPTION];
          setRegionOptions([GEO_ALL_OPTION]);
          return;
        }
        const regionJobs = [];
        for (const zoneOptionId of zSel) {
          const zrow = (zonesList || []).find((x) => x.id === zoneOptionId);
          const linkedCountries = Array.isArray(zrow?.countryCodes)
            ? zrow.countryCodes
            : zrow?.countryCode
              ? [zrow.countryCode]
              : [];
          const countriesToQuery = exportMode
            ? cSel.filter((cc) => !linkedCountries.length || linkedCountries.includes(cc))
            : linkedCountries.length === 1
              ? [linkedCountries[0]]
              : linkedCountries.length
                ? linkedCountries
                : cSel;
          for (const cc of countriesToQuery) {
            if (!cc || isGeoAllSelectionToken(cc)) continue;
            const zoneCode = exportMode
              ? resolveZoneCodeForCountry(zoneOptionId, cc, zonesList)
              : zoneOptionId;
            if (!zoneCode) continue;
            regionJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getRegions(cc, zoneCode);
                const raw = extractRegionsFromResponse(res);
                const active = Array.isArray(raw)
                  ? raw.filter((r) => r.isActive !== false && r.IsActive !== false)
                  : [];
                return active;
              })()
            );
          }
        }
        const regionResults = await Promise.all(regionJobs);
        const merged = [];
        regionResults.forEach((active) => {
          active.forEach((r) => merged.push(normalizeRegionRow(r)));
        });
        const byReg = new Map();
        merged.forEach((r) => {
          if (!r.regionCode || isGeoAllOptionRow(r.regionCode, r.regionName)) return;
          byReg.set(r.regionCode, mapRegionToOption(r));
        });
        const options = [GEO_ALL_OPTION, ...byReg.values()];
        regionOptionsRef.current = options;
        setRegionOptions(options);
      } catch (e) {
        console.error("Error loading Region options:", e);
        regionOptionsRef.current = [GEO_ALL_OPTION];
        setRegionOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadTerritoryOptions = async (
    regionCodes,
    zonesList,
    regionsList,
    filterZoneCodes,
    filterCountryCodes
  ) => {
    await runGeoDropdownLoad("territory", async () => {
      try {
        const rSel = Array.isArray(regionCodes)
          ? regionCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!rSel.length) {
          setTerritoryOptions([GEO_ALL_OPTION]);
          return;
        }
        const regionsListResolved =
          (Array.isArray(regionsList) && regionsList.length
            ? regionsList
            : regionOptionsRef.current) || [];
        const zonesListResolved =
          (Array.isArray(zonesList) && zonesList.length
            ? zonesList
            : zoneOptionsRef.current) || [];
        const territoryJobs = [];
        for (const rc of rSel) {
          const row = regionsListResolved.find((x) => x.id === rc);
          const pairs = resolveRegionGeoCandidates(
            rc,
            row,
            regionsListResolved,
            zonesListResolved,
            filterZoneCodes,
            filterCountryCodes
          );
          if (!pairs.length) continue;
          for (const { cc, zc } of pairs) {
            if (isGeoAllSelectionToken(cc) || isGeoAllSelectionToken(zc)) continue;
            territoryJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getPaged({
                  countryCode: cc,
                  zoneCode: zc,
                  regionCode: rc,
                  status: "active",
                  pageNumber: 1,
                  pageSize: 5000,
                });
                const { ok, payload } = parseApiEnvelope(res);
                const items =
                  ok && payload ? payload.territories || payload.Territories || [] : [];
                return Array.isArray(items)
                  ? items.filter((t) => t.isActive !== false && t.IsActive !== false)
                  : [];
              })()
            );
          }
        }
        const territoryResults = await Promise.all(territoryJobs);
        const merged = [];
        territoryResults.forEach((active) => {
          active.forEach((t) => merged.push(normalizeTerritoryRow(t)));
        });
        const byT = new Map();
        merged.forEach((t) => {
          if (!t.territoryCode || isGeoAllOptionRow(t.territoryCode, t.territoryName)) return;
          byT.set(t.territoryCode, mapTerritoryToOption(t));
        });
        setTerritoryOptions([GEO_ALL_OPTION, ...byT.values()]);
      } catch (e) {
        console.error("Error loading Territory options:", e);
        setTerritoryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const handleGeoFilterChange = (field, value, optionList = []) => {
    const exportCascade = isExportMarketSelected(filters.market, marketOptions);
    const raw = Array.isArray(value)
      ? value.map((x) => String(x ?? "").trim()).filter(Boolean)
      : [];
    const realOnly = raw.filter((id) => {
      const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === id);
      return !isGeoAllOptionRow(id, opt?.text);
    });
    const pickedAll = selectionIncludesGeoAll(raw, optionList);
    const prevWasAll = isGeoAllFilterValue(filters[field]);

    if (pickedAll && !(prevWasAll && realOnly.length > 0)) {
      const allVal = [USER_DEFAULT_GEO_CODE];
      // All = no filter on this level. Do not lock child dropdowns to All.
      if (field === "country") {
        setFilters((prev) => ({
          ...prev,
          country: allVal,
          zone: [],
          region: [],
          territory: [],
        }));
        return;
      }
      if (field === "zone") {
        if (exportCascade) {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            country: [],
            region: [],
            territory: [],
          }));
        } else {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            region: [],
            territory: [],
          }));
        }
        return;
      }
      if (field === "region") {
        setFilters((prev) => ({ ...prev, region: allVal, territory: [] }));
        return;
      }
      if (field === "territory") {
        setFilters((prev) => ({ ...prev, territory: allVal }));
        return;
      }
    }

    const selected = realOnly;
    setFilters((prev) => {
      if (field === "country") {
        if (exportCascade) {
          return { ...prev, country: selected, region: [], territory: [] };
        }
        return { ...prev, country: selected, zone: [], region: [], territory: [] };
      }
      if (field === "zone") {
        if (exportCascade) {
          return { ...prev, zone: selected, country: [], region: [], territory: [] };
        }
        return { ...prev, zone: selected, region: [], territory: [] };
      }
      if (field === "region") {
        return { ...prev, region: selected, territory: [] };
      }
      if (field === "territory") {
        return { ...prev, territory: selected };
      }
      return prev;
    });
  };

  const handleBvChange = (selected) => {
    const raw = Array.isArray(selected) ? selected : [];
    if (selectionIncludesGeoAll(raw, bvOptions)) {
      setFilters((prev) => ({ ...prev, bv: [USER_DEFAULT_GEO_CODE] }));
      return;
    }
    setFilters((prev) => ({
      ...prev,
      bv: raw.filter((id) => !isGeoAllSelectionToken(id)),
    }));
  };

  const handleRoleChange = (selected) => {
    const raw = Array.isArray(selected)
      ? selected.map((x) => String(x ?? "").trim()).filter(Boolean)
      : [];
    const realOnly = raw.filter((id) => !isRoleAllToken(id));
    const pickedAll = raw.some((id) => isRoleAllToken(id));
    const prevWasAll = isRoleAllFilterValue(filters.role);

    if (pickedAll && !(prevWasAll && realOnly.length > 0)) {
      setFilters((prev) => ({ ...prev, role: ["all"] }));
      return;
    }
    setFilters((prev) => ({ ...prev, role: realOnly }));
  };

  const handleMarketChange = (nextMarket) => {
    const market = isGeoAllSelectionToken(nextMarket) ? "" : nextMarket || "";
    setFilters({
      ...EMPTY_GEO,
      market,
    });
    setBvOptions([]);
    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
  };

  const resetFilters = () => {
    setFilters({ ...EMPTY_GEO });
    setBvOptions([]);
    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
  };

  const toQueryParams = useCallback(
    () => ({
      marketCode: filters.market || undefined,
      bvCode: toCsv(filters.bv),
      userTypeCode: toRoleCsv(filters.role),
      countryCode: toCsv(filters.country),
      zoneCode: toCsv(filters.zone),
      regionCode: toCsv(filters.region),
      territoryCode: toCsv(filters.territory),
    }),
    [filters]
  );

  useEffect(() => {
    loadMarketOptions();
    loadCountryOptions();
    loadRoleOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadBVOptions(filters.market);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.market]);

  useEffect(() => {
    if (!isExportCascade) return;
    if (!filters.market) return;
    loadZoneOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade, filters.market]);

  useEffect(() => {
    if (isExportCascade) return;
    if (isGeoAllFilterValue(filters.country)) {
      loadZoneOptions();
      return;
    }
    if (filters.country?.length) {
      setZoneOptions([]);
      loadZoneOptions(filters.country);
    } else {
      setZoneOptions([]);
      setFilters((prev) => ({ ...prev, zone: [], region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade, filters.country]);

  useEffect(() => {
    if (prevExportCascadeRef.current === null) {
      prevExportCascadeRef.current = isExportCascade;
      return;
    }
    if (prevExportCascadeRef.current === isExportCascade) return;
    prevExportCascadeRef.current = isExportCascade;
    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
    setFilters((prev) => ({
      ...prev,
      country: [],
      zone: [],
      region: [],
      territory: [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  useEffect(() => {
    if (!zoneOptions.length || !countryOptions.length) return;
    if (isGeoAllFilterValue(filters.zone)) return;
    setZoneOptions((prev) =>
      prev.map((z) => {
        if (isGeoAllSelectionToken(z.id) || isGeoAllOptionRow(z.id, z.text)) return z;
        return {
          ...z,
          text: formatZoneOptionLabel(z, countryOptions, {
            includeCountry: !isExportCascade,
          }),
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, isExportCascade]);

  useEffect(() => {
    const zAll = isGeoAllFilterValue(filters.zone);
    const cAll = isGeoAllFilterValue(filters.country);
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (zAll) {
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (zOk && cOk) {
      const zoneArg = cAll ? filters.zone : filters.zone;
      setRegionOptions([]);
      regionOptionsRef.current = [];
      loadRegionOptions(zoneArg, cAll ? [] : filters.country, zoneOptions, isExportCascade);
    } else {
      setRegionOptions([]);
      regionOptionsRef.current = [];
      setFilters((prev) => ({ ...prev, region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.zone, filters.country, isExportCascade, zoneOptions]);

  useEffect(() => {
    if (isGeoAllFilterValue(filters.region)) {
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (!filters.region?.length) {
      setTerritoryOptions([]);
      setFilters((prev) => ({ ...prev, territory: [] }));
      return;
    }
    const regionsReady =
      regionOptions.length > 0 || regionOptionsRef.current.length > 0;
    if (!regionsReady) return;
    setTerritoryOptions([]);
    loadTerritoryOptions(
      filters.region,
      zoneOptions.length ? zoneOptions : zoneOptionsRef.current,
      regionOptions.length ? regionOptions : regionOptionsRef.current,
      filters.zone,
      filters.country
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.zone, filters.country, regionOptions, zoneOptions]);

  return {
    filterExpanded,
    setFilterExpanded,
    filters,
    setFilters,
    marketOptions,
    bvOptions,
    roleOptions,
    countryOptions,
    zoneOptions,
    regionOptions,
    territoryOptions,
    geoLoading,
    isExportCascade,
    exportCountryOptions,
    isGeoAllFilterValue,
    isRoleAllFilterValue,
    handleGeoFilterChange,
    handleMarketChange,
    handleBvChange,
    handleRoleChange,
    resetFilters,
    toQueryParams,
  };
}
